<template>
  <div
    ref="triggerRef"
    @mouseenter="show"
    @mouseleave="scheduleHide"
    @focusin="onFocusIn"
    @focusout="hide"
    @keydown.escape="onEscape"
  >
    <slot />

    <!-- Teleported so the scrolling sidebar can't clip it. Hidden from
         assistive technology: the trigger already carries the same text as
         its accessible name. -->
    <Teleport to="body">
      <div
        v-if="open"
        aria-hidden="true"
        class="fixed z-50 -translate-y-1/2 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium shadow-dropdown bg-heading dark:bg-heading-dark text-box-container-bg dark:text-box-container-bg-dark"
        :style="{ top: `${position.top}px`, left: `${position.left}px` }"
        @mouseenter="cancelHide"
        @mouseleave="scheduleHide"
      >
        {{ text }}
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, reactive, ref, watch } from "vue";

interface Props {
  text: string;
  // Expanded sidebar: labels are visible, so no tooltip.
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), { disabled: false });

const OFFSET = 8;
// Grace period to move the pointer from the trigger onto the tooltip, which
// stays open while hovered (WCAG 1.4.13).
const HIDE_DELAY_MS = 100;

const triggerRef = ref<HTMLDivElement | null>(null);
const open = ref(false);
const position = reactive({ top: 0, left: 0 });
let hideTimer: ReturnType<typeof setTimeout> | undefined;

const cancelHide = () => {
  clearTimeout(hideTimer);
  hideTimer = undefined;
};

const show = () => {
  cancelHide();
  if (props.disabled || !triggerRef.value) return;
  const rect = triggerRef.value.getBoundingClientRect();
  position.top = rect.top + rect.height / 2;
  position.left = rect.right + OFFSET;
  open.value = true;
};

const hide = () => {
  cancelHide();
  open.value = false;
};

const scheduleHide = () => {
  cancelHide();
  hideTimer = setTimeout(hide, HIDE_DELAY_MS);
};

// Keyboard focus only: a mouse click also focuses the button, and hover
// already covers pointer users.
const onFocusIn = (event: FocusEvent) => {
  if ((event.target as HTMLElement).matches(":focus-visible")) show();
};

// Escape dismisses the tooltip; when it is closed the event passes through
// (e.g. to close the settings menu).
const onEscape = (event: KeyboardEvent) => {
  if (!open.value) return;
  event.stopPropagation();
  hide();
};

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) hide();
  },
);

// The tooltip is fixed-positioned from the trigger's rect, so any scroll
// would detach it; close it instead of tracking.
watch(open, (isOpen) => {
  if (isOpen) {
    window.addEventListener("scroll", hide, { capture: true, passive: true });
  } else {
    window.removeEventListener("scroll", hide, { capture: true });
  }
});

onUnmounted(() => {
  cancelHide();
  window.removeEventListener("scroll", hide, { capture: true });
});
</script>
